import { useCallback, useEffect, useRef, useState } from 'react'
import { BoxSelect, LassoSelect, MousePointer2 } from 'lucide-react'
import { getSelectedBarIndices } from '@/lib/dragSelectUtils'
import { useExpanded } from '@/components/ui/expandable'
import { SELECTION_COLORS } from '@/lib/constants'

/** Renders the rectangle or lasso outline while a drag is in progress. */
function SelectionOverlay({ shape }) {
  if (!shape) return null

  if (shape.type === 'lasso') {
    if (shape.points.length < 2) return null
    const points = shape.points.map((p) => `${p.x},${p.y}`).join(' ')
    return (
      <svg aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 h-full w-full overflow-visible">
        <polygon
          points={points}
          fill={SELECTION_COLORS.fill12}
          stroke={SELECTION_COLORS.stroke70}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  const { rect } = shape
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        background: SELECTION_COLORS.fill12,
        border: `1.5px solid ${SELECTION_COLORS.stroke60}`,
        borderRadius: 2,
        pointerEvents: 'none',
        zIndex: 20,
      }}
    />
  )
}

/** Highlights the chart elements currently inside the selection shape. */
function HighlightOverlay({ highlights }) {
  if (!highlights || highlights.length === 0) return null

  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 15 }}>
      {highlights.map((highlight, index) => {
        if (highlight.type === 'point') {
          const radius = highlight.radius || 8
          return (
            <div
              key={`point-${index}`}
              style={{
                position: 'absolute',
                left: highlight.x - radius,
                top: highlight.y - radius,
                width: radius * 2,
                height: radius * 2,
                border: `2px solid ${SELECTION_COLORS.stroke75}`,
                borderRadius: 999,
                background: SELECTION_COLORS.fill10,
                boxShadow: `0 0 0 2px ${SELECTION_COLORS.shadow14}`,
              }}
            />
          )
        }

        return (
          <div
            key={`rect-${index}`}
            style={{
              position: 'absolute',
              left: highlight.x,
              top: highlight.y,
              width: highlight.width,
              height: highlight.height,
              background: SELECTION_COLORS.fill15,
              borderRadius: 2,
            }}
          />
        )
      })}
    </div>
  )
}

const EMPTY_DETECTION = { indices: [], highlights: [] }

function rectFromClientRect(clientRect, containerRect) {
  return {
    x: clientRect.left - containerRect.left,
    y: clientRect.top - containerRect.top,
    width: clientRect.width,
    height: clientRect.height,
  }
}

function getElementRect(element, containerRect) {
  if (!element) return null

  let bounds = element.getBoundingClientRect()
  if (bounds.width === 0 && bounds.height === 0) {
    const child = element.querySelector?.('path, rect, circle, ellipse, polygon')
    bounds = child?.getBoundingClientRect?.()
  }
  if (!bounds || (bounds.width === 0 && bounds.height === 0)) return null
  return rectFromClientRect(bounds, containerRect)
}

function pointInRect(point, rect) {
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height
}

function rectsIntersect(a, b) {
  return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y
}

function pointInPolygon(point, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]
    const b = polygon[j]
    const crosses = (a.y > point.y) !== (b.y > point.y)
      && point.x < ((b.x - a.x) * (point.y - a.y)) / ((b.y - a.y) || Number.EPSILON) + a.x
    if (crosses) inside = !inside
  }
  return inside
}

function rectCenter(rect) {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }
}

function shapeContainsPoint(shape, point) {
  return shape.type === 'lasso' ? pointInPolygon(point, shape.points) : pointInRect(point, shape.rect)
}

function shapeIntersectsRect(shape, rect) {
  if (shape.type !== 'lasso') return rectsIntersect(rect, shape.rect)
  if (pointInPolygon(rectCenter(rect), shape.points)) return true
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ]
  return corners.some((p) => pointInPolygon(p, shape.points)) || shape.points.some((p) => pointInRect(p, rect))
}

function shapeBoundingRect(shape) {
  if (shape.type !== 'lasso') return shape.rect
  const xs = shape.points.map((p) => p.x)
  const ys = shape.points.map((p) => p.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }
}

function mergeRects(rects) {
  if (!rects.length) return null
  const left = Math.min(...rects.map((rect) => rect.x))
  const top = Math.min(...rects.map((rect) => rect.y))
  const right = Math.max(...rects.map((rect) => rect.x + rect.width))
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height))
  return { type: 'rect', x: left, y: top, width: right - left, height: bottom - top }
}

function detectBarSelection(container, shape, chartData, chartMargin) {
  const barElements = Array.from(container.querySelectorAll('.recharts-bar-rectangle'))
  if (barElements.length === 0) {
    const indices = getSelectedBarIndices({
      selectionRect: shapeBoundingRect(shape),
      dataLength: chartData.length,
      chartMargin,
      containerWidth: container.offsetWidth,
    })
    return { indices, highlights: [] }
  }

  const containerRect = container.getBoundingClientRect()
  const selectedIndices = new Set()
  const rectsByIndex = new Map()

  barElements.forEach((element, elementIndex) => {
    const dataIndex = elementIndex % chartData.length
    const elementRect = getElementRect(element, containerRect)
    if (!elementRect) return
    const center = rectCenter(elementRect)

    // Select a bar when the shape actually overlaps it, so dragging a small box
    // over one bar grabs that bar and not its neighbours.
    const hit = shape.type === 'lasso'
      ? shapeContainsPoint(shape, center) || shapeIntersectsRect(shape, elementRect)
      : rectsIntersect(elementRect, shape.rect)

    if (hit) {
      selectedIndices.add(dataIndex)
      const rects = rectsByIndex.get(dataIndex) || []
      rects.push(elementRect)
      rectsByIndex.set(dataIndex, rects)
    }
  })

  const indices = Array.from(selectedIndices).sort((a, b) => a - b)
  return { indices, highlights: indices.map((index) => mergeRects(rectsByIndex.get(index) || [])).filter(Boolean) }
}

function detectScatterSelection(container, shape, chartData, scatterDataSets) {
  const symbolElements = Array.from(container.querySelectorAll('.recharts-scatter-symbol'))
  if (symbolElements.length === 0) return EMPTY_DETECTION

  const dataInRenderOrder = Array.isArray(scatterDataSets) && scatterDataSets.length > 0
    ? scatterDataSets.flat()
    : chartData
  const containerRect = container.getBoundingClientRect()
  const selectedIndices = new Set()
  const highlights = []

  symbolElements.forEach((element, elementIndex) => {
    const datum = dataInRenderOrder[elementIndex]
    const dataIndex = chartData.indexOf(datum)
    if (dataIndex < 0) return
    const elementRect = getElementRect(element, containerRect)
    if (!elementRect) return
    const center = rectCenter(elementRect)
    if (!shapeContainsPoint(shape, center)) return
    selectedIndices.add(dataIndex)
    highlights.push({ type: 'point', x: center.x, y: center.y, radius: Math.max(7, elementRect.width / 2 + 4) })
  })

  return { indices: Array.from(selectedIndices).sort((a, b) => a - b), highlights }
}

function detectIndexedSelection(container, shape, chartData, selector, getIndex) {
  const elements = Array.from(container.querySelectorAll(selector))
  if (elements.length === 0) return EMPTY_DETECTION
  const containerRect = container.getBoundingClientRect()
  const selectedIndices = new Set()
  const highlights = []

  elements.forEach((element, elementIndex) => {
    const dataIndex = getIndex(element, elementIndex)
    if (!Number.isInteger(dataIndex) || !chartData[dataIndex]) return
    const elementRect = getElementRect(element, containerRect)
    if (!elementRect) return
    if (!(shapeContainsPoint(shape, rectCenter(elementRect)) || shapeIntersectsRect(shape, elementRect))) return
    selectedIndices.add(dataIndex)
    highlights.push({ type: 'rect', ...elementRect })
  })

  return { indices: Array.from(selectedIndices).sort((a, b) => a - b), highlights }
}

/**
 * Tool control rendered in a thin row above the chart (outside the draggable
 * plot, which can have data in any corner). On touch it arms drag selection.
 */
function ToolControl({ isTouch, tool, setTool, touchEnabled, setTouchEnabled }) {
  if (isTouch) {
    return (
      <button
        type="button"
        data-drag-select-control="true"
        aria-pressed={touchEnabled}
        aria-label={touchEnabled ? 'Disable drag selection' : 'Enable drag selection'}
        title={touchEnabled ? 'Disable drag selection' : 'Drag to select'}
        onClick={() => setTouchEnabled((value) => !value)}
        className={[
          'flex h-7 items-center gap-1.5 border px-2 text-[11px] transition-colors',
          touchEnabled ? 'border-primary text-foreground ring-1 ring-primary/20' : 'border-border text-muted-foreground',
        ].join(' ')}
      >
        <MousePointer2 className="h-3.5 w-3.5" />
        {touchEnabled ? 'Selecting' : 'Drag-select'}
      </button>
    )
  }

  const btn = (active) => [
    'flex h-6 w-7 items-center justify-center transition-colors',
    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
  ].join(' ')

  return (
    <div
      data-drag-select-control="true"
      className="flex items-center border border-border"
      role="group"
      aria-label="Selection shape"
    >
      <button type="button" className={btn(tool === 'rectangle')} aria-pressed={tool === 'rectangle'} title="Box select (drag)" onClick={() => setTool('rectangle')}>
        <BoxSelect className="h-3.5 w-3.5" />
      </button>
      <button type="button" className={btn(tool === 'lasso')} aria-pressed={tool === 'lasso'} title="Lasso select (drag a freehand loop)" onClick={() => setTool('lasso')}>
        <LassoSelect className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

/**
 * Wraps a chart to add box / lasso drag selection on top of normal clicks.
 * A short press is still a chart click; dragging past a small threshold draws
 * the active shape. Holding Shift while clicking or dragging adds to the running
 * selection instead of replacing it. On touch, selection is armed with the
 * on-chart button so the page can still scroll.
 */
export function DragSelectWrapper({
  children,
  chartData,
  chartType,
  onGroupSelect,
  getEmployeeIds,
  getTitle,
  getDescription,
  // Chart-specific dialog column descriptors, forwarded on the emitted group.
  groupColumns,
  color,
  height,
  chartMargin,
  scatterDataSets,
}) {
  const containerRef = useRef(null)
  const expanded = useExpanded()
  const [tool, setTool] = useState('rectangle')
  const [isTouch, setIsTouch] = useState(false)
  const [touchEnabled, setTouchEnabled] = useState(false)
  const [matchedHighlights, setMatchedHighlights] = useState([])
  const [activeShape, setActiveShape] = useState(null)
  const suppressClickRef = useRef(false)
  const dragStateRef = useRef({ active: false, pending: false, startX: 0, startY: 0, pointerId: null, additive: false, points: [] })

  const runHitDetection = useCallback((shape) => {
    const container = containerRef.current
    if (!container || !shape || !chartData?.length) return EMPTY_DETECTION
    if (chartType === 'bar') return detectBarSelection(container, shape, chartData, chartMargin)
    if (chartType === 'scatter') return detectScatterSelection(container, shape, chartData, scatterDataSets)
    if (chartType === 'grid') {
      return detectIndexedSelection(container, shape, chartData, '[data-drag-select-index]', (el) => Number.parseInt(el.dataset.dragSelectIndex, 10))
    }
    if (chartType === 'pie') {
      return detectIndexedSelection(container, shape, chartData, '.recharts-pie-sector', (_el, index) => index % chartData.length)
    }
    return EMPTY_DETECTION
  }, [chartData, chartType, chartMargin, scatterDataSets])

  const finishDrag = useCallback((shape, additive) => {
    if (shape) {
      const { indices } = runHitDetection(shape)
      if (indices.length > 0) {
        const selectedItems = indices.map((i) => chartData[i]).filter(Boolean)
        const employeeIds = Array.from(new Set(selectedItems.flatMap((item) => getEmployeeIds(item))))
        if (employeeIds.length > 0) {
          onGroupSelect?.({
            title: getTitle(selectedItems),
            description: getDescription(selectedItems),
            employeeIds,
            color,
            columns: groupColumns,
            additive: Boolean(additive),
          })
        }
      }
    }
    setMatchedHighlights([])
    setActiveShape(null)
    if (isTouch) setTouchEnabled(false)
    dragStateRef.current.active = false
    dragStateRef.current.pending = false
  }, [runHitDetection, chartData, getEmployeeIds, getTitle, getDescription, groupColumns, color, onGroupSelect, isTouch])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(pointer: coarse), (max-width: 767px)')
    const handleChange = () => {
      setIsTouch(mediaQuery.matches)
      if (!mediaQuery.matches) setTouchEnabled(false)
    }
    handleChange()
    mediaQuery.addEventListener?.('change', handleChange)
    mediaQuery.addListener?.(handleChange)
    return () => {
      mediaQuery.removeEventListener?.('change', handleChange)
      mediaQuery.removeListener?.(handleChange)
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const THRESHOLD = 5
    let suppressClickTimer = null

    function suppressNextClick() {
      suppressClickRef.current = true
      if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer)
      suppressClickTimer = window.setTimeout(() => {
        suppressClickRef.current = false
        suppressClickTimer = null
      }, 0)
    }

    function handleClickCapture(e) {
      if (!suppressClickRef.current) return
      suppressClickRef.current = false
      e.preventDefault()
      e.stopPropagation()
    }

    function shapeFor(state, currentX, currentY) {
      if (tool === 'lasso') {
        const previous = state.points[state.points.length - 1]
        if (!previous || Math.hypot(currentX - previous.x, currentY - previous.y) >= 3) {
          state.points.push({ x: currentX, y: currentY })
        }
        return { type: 'lasso', points: state.points }
      }
      return {
        type: 'rectangle',
        rect: {
          x: Math.min(state.startX, currentX),
          y: Math.min(state.startY, currentY),
          width: Math.abs(currentX - state.startX),
          height: Math.abs(currentY - state.startY),
        },
      }
    }

    function handlePointerDown(e) {
      if (e.button !== 0) return
      if (e.target?.closest?.('[data-drag-select-control]')) return
      if (e.pointerType === 'touch' && isTouch && !touchEnabled) return

      const bounds = container.getBoundingClientRect()
      const x = e.clientX - bounds.left
      const y = e.clientY - bounds.top
      dragStateRef.current = { active: false, pending: true, startX: x, startY: y, pointerId: e.pointerId, additive: e.shiftKey, points: [{ x, y }] }
      document.addEventListener('pointermove', handlePointerMove, { passive: false })
      document.addEventListener('pointerup', handlePointerUp)
    }

    function handlePointerMove(e) {
      const state = dragStateRef.current
      if (!state.pending && !state.active) return
      const bounds = container.getBoundingClientRect()
      const currentX = Math.max(0, Math.min(e.clientX - bounds.left, bounds.width))
      const currentY = Math.max(0, Math.min(e.clientY - bounds.top, bounds.height))

      if (state.pending && !state.active) {
        if (Math.hypot(currentX - state.startX, currentY - state.startY) < THRESHOLD) return
        state.pending = false
        state.active = true
        try { container.setPointerCapture(state.pointerId) } catch (err) { /* best effort */ }
      }

      if (!state.active) return
      e.preventDefault()
      const shape = shapeFor(state, currentX, currentY)
      setActiveShape(shape.type === 'lasso' ? { type: 'lasso', points: [...shape.points] } : shape)
      setMatchedHighlights(runHitDetection(shape).highlights)
    }

    function handlePointerUp(e) {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      const state = dragStateRef.current
      try { container.releasePointerCapture(state.pointerId) } catch (err) { /* best effort */ }

      if (!state.active) {
        state.pending = false
        return
      }
      suppressNextClick()
      e.preventDefault()
      const bounds = container.getBoundingClientRect()
      const currentX = Math.max(0, Math.min(e.clientX - bounds.left, bounds.width))
      const currentY = Math.max(0, Math.min(e.clientY - bounds.top, bounds.height))
      finishDrag(shapeFor(state, currentX, currentY), state.additive)
    }

    function handleKeyDown(e) {
      if (e.key !== 'Escape' || !dragStateRef.current.active) return
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      try { container.releasePointerCapture(dragStateRef.current.pointerId) } catch (err) { /* best effort */ }
      finishDrag(null)
    }

    container.addEventListener('pointerdown', handlePointerDown, true)
    container.addEventListener('click', handleClickCapture, true)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer)
      container.removeEventListener('pointerdown', handlePointerDown, true)
      container.removeEventListener('click', handleClickCapture, true)
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [runHitDetection, finishDrag, tool, isTouch, touchEnabled])

  return (
    <div className="select-none">
      <div className="mb-1 flex justify-end">
        <ToolControl isTouch={isTouch} tool={tool} setTool={setTool} touchEnabled={touchEnabled} setTouchEnabled={setTouchEnabled} />
      </div>
      <div
        ref={containerRef}
        className="relative"
        style={{
          height: expanded ? '58vh' : height,
          cursor: isTouch && !touchEnabled ? 'default' : 'crosshair',
          touchAction: isTouch && touchEnabled ? 'none' : 'pan-y',
        }}
      >
        {children}
        {activeShape && <SelectionOverlay shape={activeShape} />}
        {activeShape && <HighlightOverlay highlights={matchedHighlights} />}
      </div>
    </div>
  )
}
